import {Active, Over} from '@dnd-kit/core';
import {useCallback} from 'react';

import {PanelDropRef} from '../../types';
import {getWorkspaceDropIndicatorPosition} from '../utils';

export const useHandleDragOver = ({
  panelDropIndicatorRef,
  panelDropRef,
}: {
  panelDropIndicatorRef: React.RefObject<HTMLDivElement>;
  panelDropRef: React.MutableRefObject<PanelDropRef | null>;
}) => {
  // We add a custom drag over handler so we can determine where to show
  // the vertical drop indicator when a panel is dragging over a DIFFERENT section.
  // If panel is dragging over the same section, we don't need to show the indicator.
  // The shifting gets handled by the SortableContext.
  const handleDragOver = useCallback(
    (active: Active | null, over: Over | null) => {
      const indicator = panelDropIndicatorRef.current;
      if (!indicator) {
        return;
      }

      const activeData = active?.data.current;
      const overData = over?.data.current;

      if (
        !activeData ||
        !over ||
        !overData ||
        Object.keys(overData).length === 0 ||
        Object.keys(activeData).length === 0
      ) {
        indicator.style.opacity = '0';
        panelDropRef.current = null;
        console.error(
          'handleDragOver has no "active" or "over" ref data. This should not happen.'
        );
        return;
      }

      // We only want to show the drop indicator when a panel is being dragged
      if (activeData.type === 'panel') {
        handlePanelDragOver({
          active,
          over,
          indicator,
          panelDropRef,
        });
      }
    },
    [panelDropIndicatorRef, panelDropRef]
  );

  return handleDragOver;
};

const handlePanelDragOver = ({
  active,
  over,
  indicator,
  panelDropRef,
}: {
  active: Active;
  over: Over;
  indicator: HTMLDivElement;
  panelDropRef: React.MutableRefObject<PanelDropRef | null>;
}) => {
  const activeData = active.data.current;
  const overData = over.data.current;

  // We only want to show the drop indicator when a panel is being dragged
  // Reorder section should show a horizontal drop indicator instead
  if (!activeData || !overData || activeData.type !== 'panel') {
    indicator.style.opacity = '0';
    panelDropRef.current = null;
    return;
  }

  const isSameSection = activeData.sectionRef?.id === overData?.sectionRef?.id;

  // SortableContext will handle the panel reordering within the section.
  // We want to show the vertical drop indicator for panel-to-section drops
  // and simplify shifting logic.
  if (isSameSection) {
    indicator.style.opacity = '0';
    panelDropRef.current = null;
    return;
  }

  const overRect = over.rect;
  const activeRect = active.rect.current.translated;

  if (!activeRect || !overRect) {
    indicator.style.opacity = '0';
    panelDropRef.current = null;
    return;
  }

  if (overData.type === 'section') {
    indicator.style.opacity = '0';

    panelDropRef.current = {
      sectionRef: overData.sectionRef,
      panelRef: undefined, // we use this to indicate that section is empty
      // section is empty or collapsed, insert panel to the beginning of the section
      // otherwise insert to the end
      insertDir:
        overData.filteredPanelsCount === 0 || !overData.isSectionExpanded
          ? 'before'
          : 'after',
    };

    return;
  }

  // We want the vertical drop indicator to be centered between the gap between panels
  const {isRightSide, xPosition, yPosition} = getWorkspaceDropIndicatorPosition(
    activeRect,
    overRect
  );

  // Ensure we are using transforms instead of absolute positioning for performance optimizations
  indicator.style.opacity = '1';
  indicator.style.height = `${overRect.height}px`;
  indicator.style.transform = `translate(${xPosition}px, ${yPosition}px)`;

  // Update drop state so we know where panel should be inserted
  panelDropRef.current = {
    sectionRef: overData.sectionRef,
    panelRef: overData.panelRef,
    insertDir: isRightSide ? 'after' : 'before',
  };
};
